import React from 'react';
import { Link } from 'react-router-dom';
import PublicNav from '@/components/PublicNav';
import { ArrowRight, Sparkles, Zap, Shield, ChevronRight } from 'lucide-react';

const HomePage = () => {
  return (
    <div className="min-h-screen bg-[#030712]">
      <PublicNav />
      
      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center px-6 md:px-12 pt-20" data-testid="hero-section">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7 animate-fade-in">
            <div className="inline-flex items-center space-x-2 bg-indigo-500/10 border border-indigo-500/20 rounded-full px-4 py-2 mb-6">
              <Sparkles className="text-indigo-400" size={16} />
              <span className="text-sm text-indigo-300 font-medium">Premium Web Design Agency</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold font-['Syne'] tracking-tight leading-[1.1] mb-6" data-testid="hero-title">
              <span className="text-shine">Professional</span>
              <br />
              <span className="text-white">Web & Network</span>
              <br />
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-400">Solutions</span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8 max-w-2xl" data-testid="hero-description">
              Expert website design and home networking services. 
              Access your exclusive client portal for seamless project management and support.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/pricing" 
                className="bg-white text-black hover:bg-slate-200 font-semibold px-8 py-3 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-[0_0_20px_rgba(255,255,255,0.3)] inline-flex items-center justify-center space-x-2"
                data-testid="hero-cta-pricing"
              >
                <span>View Plans</span>
                <ArrowRight size={20} />
              </Link>
              <Link 
                to="/contact" 
                className="bg-transparent border border-white/20 text-white hover:bg-white/10 px-8 py-3 rounded-full transition-all duration-300 inline-flex items-center justify-center"
                data-testid="hero-cta-contact"
              >
                Get In Touch
              </Link>
            </div>
          </div>
          
          <div className="md:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-3xl opacity-20 blur-3xl"></div>
              <img 
                src="https://images.unsplash.com/photo-1631109782087-2344fc513f7d" 
                alt="Modern architecture" 
                className="relative rounded-2xl border border-white/10 shadow-2xl"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-32 px-6 md:px-12" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-['Syne'] text-white mb-4" data-testid="features-title">Why Choose Us</h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">Premium service, exclusive access, exceptional results</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-slate-900/50 border border-white/10 rounded-2xl p-8 hover:border-indigo-500/50 transition-colors duration-500 group" data-testid="feature-card-1">
              <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center mb-4 group-hover:bg-indigo-500/20 transition-colors">
                <Zap className="text-indigo-400" size={24} />
              </div>
              <h3 className="text-2xl font-semibold font-['Syne'] text-white mb-3">Lightning Fast</h3>
              <p className="text-slate-400 leading-relaxed">Optimized for performance and conversion. Every millisecond counts.</p>
            </div>
            
            <div className="bg-slate-900/50 border border-white/10 rounded-2xl p-8 hover:border-indigo-500/50 transition-colors duration-500 group" data-testid="feature-card-2">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mb-4 group-hover:bg-purple-500/20 transition-colors">
                <Shield className="text-purple-400" size={24} />
              </div>
              <h3 className="text-2xl font-semibold font-['Syne'] text-white mb-3">Secure Portal</h3>
              <p className="text-slate-400 leading-relaxed">Invite-only client access. Your projects, protected and private.</p>
            </div>
            
            <div className="bg-slate-900/50 border border-white/10 rounded-2xl p-8 hover:border-indigo-500/50 transition-colors duration-500 group" data-testid="feature-card-3">
              <div className="w-12 h-12 rounded-xl bg-pink-500/10 flex items-center justify-center mb-4 group-hover:bg-pink-500/20 transition-colors">
                <Sparkles className="text-pink-400" size={24} />
              </div>
              <h3 className="text-2xl font-semibold font-['Syne'] text-white mb-3">Premium Quality</h3>
              <p className="text-slate-400 leading-relaxed">Handcrafted designs that make your brand unforgettable.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-6 md:px-12" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <div className="glass rounded-3xl p-12 md:p-16 glow">
            <h2 className="text-4xl md:text-5xl font-bold font-['Syne'] text-white mb-6" data-testid="cta-title">
              Ready to Start Your Project?
            </h2>
            <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
              Join our exclusive community of successful businesses. Get started with a plan that fits your needs.
            </p>
            <Link 
              to="/pricing" 
              className="bg-white text-black hover:bg-slate-200 font-semibold px-8 py-4 rounded-full transition-all duration-300 transform hover:scale-105 inline-flex items-center space-x-2"
              data-testid="cta-button"
            >
              <span>View Pricing Plans</span>
              <ChevronRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12 px-6" data-testid="footer">
        <div className="max-w-7xl mx-auto text-center text-slate-500">
          <p>&copy; 2026 DH HOME NETWORKING AND WEBSITES. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
